import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    Building2,
    Check,
    Dumbbell,
    Flag,
    Loader2,
    RefreshCw,
    Search,
    X,
} from 'lucide-react';

function confirmApprove(correction, schedeCount) {
    const target = correction.proposed_label;
    if (correction.field === 'is_cardio') {
        return window.confirm(
            `Applicare “${target}” al catalogo per tutte le palestre?\n\nL'esercizio è usato in ${schedeCount} schede. I numeri già salvati restano, ma da questo momento vengono letti come minuti oppure come ripetizioni, in base al nuovo tipo.`
        );
    }
    return window.confirm(
        `Applicare “${target}” al catalogo?\n\nLo vedranno tutte le palestre. L'esercizio è usato in ${schedeCount} schede.`
    );
}

export default function ExerciseCorrectionsPanel({ onReviewed }) {
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();

    const [exercises, setExercises] = useState([]);
    const [stats, setStats] = useState({ exercises: 0, corrections: 0, gyms: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [search, setSearch] = useState('');
    const [busyId, setBusyId] = useState(null);

    const fetchReview = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/gym_exercise_corrections.php', {
                params: { action: 'admin_review' },
            });
            if (res.data?.success) {
                setExercises(res.data.exercises || []);
                setStats(res.data.stats || { exercises: 0, corrections: 0, gyms: 0 });
            } else {
                setError(res.data?.error || 'Impossibile caricare le correzioni');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Errore di connessione al server');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchReview();
    }, [fetchReview]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return exercises;
        return exercises.filter((ex) => {
            const hay = [
                ex.nome,
                String(ex.esercizio_id),
                ...(ex.corrections || []).flatMap((c) => [c.gym_name, c.field_label, c.current_label, c.proposed_label, c.created_by]),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [exercises, search]);

    const review = async (correction, exercise, action) => {
        if (busyId) return;
        if (action === 'approve' && !confirmApprove(correction, exercise.schede_count || 0)) return;
        if (action === 'reject' && !window.confirm(`Rifiutare la correzione di ${correction.gym_name}? Quella palestra torna a vedere il catalogo.`)) return;
        setBusyId(correction.id);
        setError('');
        try {
            const res = await api.post('/gym_exercise_corrections.php', {
                action,
                id: correction.id,
            });
            if (!res.data?.success) {
                throw new Error(res.data?.error || 'Operazione non riuscita');
            }
            setMessage(action === 'approve'
                ? `Catalogo aggiornato: ${correction.field_label} → ${correction.proposed_label}.`
                : `Correzione rifiutata per ${correction.gym_name}.`);
            await fetchReview();
            if (onReviewed) onReviewed();
            setTimeout(() => setMessage(''), 5000);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Operazione non riuscita');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Correzioni dalle palestre
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                        Tipo, muscolo e attrezzatura. La palestra li vede già. Approvi per scriverli nel catalogo, oppure rifiuti e quella palestra torna al valore attuale.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={fetchReview}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                    <RefreshCw className="h-4 w-4" />
                    Aggiorna
                </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Esercizi</p>
                    <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
                        <Dumbbell className="h-4 w-4 text-indigo-500" />
                        {stats.exercises}
                    </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Correzioni</p>
                    <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
                        <Flag className="h-4 w-4 text-amber-500" />
                        {stats.corrections}
                    </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Palestre</p>
                    <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
                        <Building2 className="h-4 w-4 text-emerald-500" />
                        {stats.gyms}
                    </p>
                </div>
            </div>

            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Cerca esercizio, palestra o valore…"
                    className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
            </div>

            {message ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
                    {message}
                </div>
            ) : null}
            {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-gray-500 dark:text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Caricamento…
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    {exercises.length === 0
                        ? 'Nessuna palestra ha ancora corretto un esercizio.'
                        : 'Nessun risultato per questa ricerca.'}
                </div>
            ) : (
                <div className="space-y-4">
                    {filtered.map((ex) => (
                        <article
                            key={ex.esercizio_id}
                            className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                        >
                            <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-900">
                                    {ex.immagine_nome ? (
                                        <img
                                            src={`/api/serve_image.php?filename=${encodeURIComponent(ex.immagine_nome)}`}
                                            alt=""
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <span className="flex h-full w-full items-center justify-center">
                                            <Dumbbell className="h-4 w-4 text-gray-400" />
                                        </span>
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <h4 className="truncate font-semibold text-gray-900 dark:text-white">{ex.nome}</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Usato in {ex.schede_count || 0} schede
                                    </p>
                                </div>
                            </div>
                            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                {(ex.corrections || []).map((correction) => (
                                    <li key={correction.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {correction.field_label}
                                                <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">{correction.gym_name}</span>
                                            </p>
                                            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                                                <span className="text-gray-400 line-through">{correction.current_label}</span>
                                                <span className="mx-1.5 text-gray-400">→</span>
                                                <span className="font-medium text-gray-900 dark:text-white">{correction.proposed_label}</span>
                                            </p>
                                            {correction.created_by ? (
                                                <p className="mt-0.5 text-xs text-gray-400">{correction.created_by}</p>
                                            ) : null}
                                        </div>
                                        <div className="flex shrink-0 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => review(correction, ex, 'approve')}
                                                disabled={busyId === correction.id}
                                                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                                Approva
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => review(correction, ex, 'reject')}
                                                disabled={busyId === correction.id}
                                                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                                Rifiuta
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
